import _ from 'lodash';
import { getCurrentConference } from '../base/conference/functions';
import { getParticipantById, getParticipantCount, isLocalParticipantModerator } from '../base/participants/functions';
import { toState } from '../base/redux/functions';
import { FEATURE_KEY } from './constants';
/**
 * Returns the rooms object for breakout rooms.
 *
 * @param {IStateful} stateful - The redux store, the redux
 * {@code getState} function, or the redux state itself.
 * @returns {Object} Object of rooms.
 */
export const getBreakoutRooms = (stateful) => toState(stateful)[FEATURE_KEY].rooms;
/**
 * Returns the main room.
 *
 * @param {IStateful} stateful - The redux store, the redux
 * {@code getState} function, or the redux state itself.
 * @returns {IRoom|undefined} The main room object, or undefined.
 */
export const getMainRoom = (stateful) => {
    const rooms = getBreakoutRooms(stateful);
    return _.find(rooms, room => Boolean(room.isMainRoom));
};
export const getRoomsInfo = (stateful) => {
    var _a, _b;
    const breakoutRooms = getBreakoutRooms(stateful);
    const conference = getCurrentConference(stateful);
    const initialRoomsInfo = {
        rooms: []
    };
    // only main roomn
    if (!breakoutRooms || Object.keys(breakoutRooms).length === 0) {
        return Object.assign(Object.assign({}, initialRoomsInfo), { rooms: [{
                    isMainRoom: true,
                    id: (_a = conference === null || conference === void 0 ? void 0 : conference.room) === null || _a === void 0 ? void 0 : _a.roomjid,
                    jid: (_b = conference === null || conference === void 0 ? void 0 : conference.room) === null || _b === void 0 ? void 0 : _b.myroomjid,
                    participants: (conference === null || conference === void 0 ? void 0 : conference.participants) && Object.keys(conference.participants).length
                        ? Object.keys(conference.participants).map(participantId => {
                            const participantItem = conference === null || conference === void 0 ? void 0 : conference.participants[participantId];
                            const storeParticipant = getParticipantById(stateful, participantItem._id);
                            return {
                                jid: participantItem._jid,
                                role: participantItem._role,
                                displayName: participantItem._displayName,
                                avatarUrl: storeParticipant === null || storeParticipant === void 0 ? void 0 : storeParticipant.loadableAvatarUrl,
                                id: participantItem._id
                            };
                        }) : []
                }] });
    }
    return Object.assign(Object.assign({}, initialRoomsInfo), { rooms: Object.keys(breakoutRooms).map(breakoutRoomKey => {
            const breakoutRoomItem = breakoutRooms[breakoutRoomKey];
            return {
                isMainRoom: Boolean(breakoutRoomItem.isMainRoom),
                id: breakoutRoomItem.id,
                jid: breakoutRoomItem.jid,
                participants: breakoutRoomItem.participants && Object.keys(breakoutRoomItem.participants).length
                    ? Object.keys(breakoutRoomItem.participants).map(participantLongId => {
                        const participantItem = breakoutRoomItem.participants[participantLongId];
                        const ids = participantLongId.split('/');
                        const storeParticipant = getParticipantById(stateful, ids.length > 1 ? ids[1] : participantItem.jid);
                        return {
                            jid: participantItem === null || participantItem === void 0 ? void 0 : participantItem.jid,
                            role: participantItem === null || participantItem === void 0 ? void 0 : participantItem.role,
                            displayName: participantItem === null || participantItem === void 0 ? void 0 : participantItem.displayName,
                            avatarUrl: storeParticipant === null || storeParticipant === void 0 ? void 0 : storeParticipant.loadableAvatarUrl,
                            id: storeParticipant ? storeParticipant.id
                                : participantLongId
                        };
                    }) : []
            };
        }) });
};
/**
 * Returns the room by Jid.
 *
 * @param {IStateful} stateful - The redux store, the redux
 * {@code getState} function, or the redux state itself.
 * @param {string} roomJid - The jid of the room.
 * @returns {IRoom|undefined} The main room object, or undefined.
 */
export const getRoomByJid = (stateful, roomJid) => {
    const rooms = getBreakoutRooms(stateful);
    return _.find(rooms, (room) => room.jid === roomJid);
};
/**
 * Returns the id of the current room.
 *
 * @param {IStateful} stateful - The redux store, the redux
 * {@code getState} function, or the redux state itself.
 * @returns {string} Room id or undefined.
 */
export const getCurrentRoomId = (stateful) => {
    const conference = getCurrentConference(stateful);
    return conference === null || conference === void 0 ? void 0 : conference.getName();
};
/**
 * Determines whether the local participant is in a breakout room.
 *
 * @param {IStateful} stateful - The redux store, the redux
 * {@code getState} function, or the redux state itself.
 * @returns {boolean}
 */
export const isInBreakoutRoom = (stateful) => {
    var _a;
    const conference = getCurrentConference(stateful);
    return (_a = conference === null || conference === void 0 ? void 0 : conference.getBreakoutRooms()) === null || _a === void 0 ? void 0 : _a.isBreakoutRoom();
};
/**
 * Returns the breakout rooms config.
 *
 * @param {IStateful} stateful - The redux store, the redux
 * {@code getState} function, or the redux state itself.
 * @returns {Object}
 */
export const getBreakoutRoomsConfig = (stateful) => {
    const state = toState(stateful);
    const { breakoutRooms = {} } = state['features/base/config'];
    return breakoutRooms;
};
/**
 * Returns whether the add breakout room button is visible.
 *
 * @param {IStateful} stateful - Global state.
 * @returns {boolean}
 */
export const isAddBreakoutRoomButtonVisible = (stateful) => {
    var _a;
    const state = toState(stateful);
    const isLocalModerator = isLocalParticipantModerator(state);
    const { conference } = state['features/base/conference'];
    const isBreakoutRoomsSupported = (_a = conference === null || conference === void 0 ? void 0 : conference.getBreakoutRooms()) === null || _a === void 0 ? void 0 : _a.isSupported();
    const { hideAddRoomButton } = getBreakoutRoomsConfig(state);
    return isLocalModerator && isBreakoutRoomsSupported && !hideAddRoomButton;
};
/**
 * Returns whether the auto assign participants to breakout rooms button is visible.
 *
 * @param {IStateful} stateful - Global state.
 * @returns {boolean}
 */
export const isAutoAssignParticipantsVisible = (stateful) => {
    const state = toState(stateful);
    const rooms = getBreakoutRooms(state);
    const inBreakoutRoom = isInBreakoutRoom(state);
    const isLocalModerator = isLocalParticipantModerator(state);
    const participantsCount = getParticipantCount(state);
    const { hideAutoAssignButton } = getBreakoutRoomsConfig(state);
    return !inBreakoutRoom
        && isLocalModerator
        && participantsCount > 2
        && Object.keys(rooms).length > 1
        && !hideAutoAssignButton;
};
